import React, { useEffect, useMemo, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useLocation } from 'react-router-dom';

import classnames from 'classnames/bind';
import { Info } from 'lucide-react';

import {
    AdminSingleSectionData,
    AdminSingleSectionMeta,
} from '@INTERFACES/admin-api/sections';

import useDocumentPermissions from '@HOOKS/useDocumentPermissions';
import usePreviewSectionBaseTextModal from '@HOOKS/store/modals/usePreviewSectionBaseTextModal';
import useSectionIntroductionTextModal from '@HOOKS/store/modals/useSectionIntroductionTextModal';
import useDocumentStructureHelpModal from '@HOOKS/store/modals/useDocumentStructureHelpModal';

import scrollToVariant from '@PAGES/admin/edit-section/helpers/scrollToVariant';
import PageHeader from '@PAGES/admin/edit-section/components/PageHeader';

import Variant from '@PAGES/admin/edit-section/components/Variant';
import VariantListSidebar from '@PAGES/admin/edit-section/components/VariantListSidebar';
import SectionIntroductionEmptyState from '@PAGES/admin/edit-section/components/SectionIntroductionEmptyState';

import FloatingPublishButton from '@PAGES/admin/edit-section/components/FloatingPublishButton';
import Mandatory from '@PAGES/admin/edit-section/components/Mandatory';

import AlertDialog from '@COMPONENTS/COMMON/dialog/dialog';
import DocumentPreviewPage from '@COMPONENTS/SHARED/DocumentPreviewPage';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import { useUpdateVariant } from '@SERVICES';
import styles from './Content.module.scss';

const cx: CX = classnames.bind(styles);

function Content(props: Props) {
    const {
        view, top, sectionData, sectionMeta,
    } = props;

    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const [showExpandButton, setShowExpandButton] = useState<boolean>(false);
    const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
    const [showSectionVariantHelp, setShowSectionVariantHelp] = useState<boolean>(false);

    const { canEditDocument } = useDocumentPermissions({
        documentOwnerId: sectionData.document.owner,
        permissions: sectionMeta.permissions,
    });

    const [updateVariant] = useUpdateVariant();

    const { hash } = useLocation();
    const { variants } = sectionData;

    const [isMandatoryOpen, setMandatoryOpen] = useState<boolean>(false);
    const [openVariantsIds, setOpenVariantsIds] = useState<Set<number>>(new Set([]));
    const [variantisDefault, setVariantsIsDefault] = useState<any>({});
    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);

    const {
        actions: previewSectionBaseTextModalActions,
    } = usePreviewSectionBaseTextModal(() => true);

    const {
        actions: sectionIntroductionTextModalActions,
    } = useSectionIntroductionTextModal(() => true);

    const {
        actions: documentStructureHelpModalActions,
    } = useDocumentStructureHelpModal(() => true);

    // Derive text data from sectionData.baseText (Section Introduction Text)
    const introductionTextData = sectionData.baseText;
    const hasIntroductionText = introductionTextData && introductionTextData.body && introductionTextData.body.trim().length > 0;
    const introductionText = introductionTextData?.body || '';

    // Track when introduction text changes to force iframe update
    React.useEffect(() => {
        if (introductionText) {
            setLastUpdateTime(Date.now());
        }
    }, [introductionText]);

    // Check if text is long enough to need truncation
    React.useEffect(() => {
        if (hasIntroductionText) {
            // Multiple checks to ensure we catch long content
            const textLength = introductionText.replace(/<[^>]*>/g, '').trim().length;
            const wordCount = introductionText.replace(/<[^>]*>/g, '').trim().split(/\s+/).length;
            const estimatedLines = Math.ceil(textLength / 70); // More conservative estimate: 70 chars per line
            
            // Multiple conditions for showing expand button - more aggressive detection
            const shouldShowExpand = (
                estimatedLines > 2 // More than 2 lines (reduced from 3)
                || textLength > 140 // More than 140 characters (reduced from 240)
                || wordCount > 25 // More than 25 words
                || (introductionText.includes('<p>') && introductionText.split('<p>').length > 2) // Multiple paragraphs
            );
            
            // Enhanced debug logging
            console.log('🔍 Section introduction text analysis:', {
                textLength,
                wordCount,
                estimatedLines,
                shouldShowExpand,
                hasIntroductionText,
                rawTextSample: `${introductionText.slice(0, 200)}...`,
                textPreview: introductionText.replace(/<[^>]*>/g, '').slice(0, 100),
            });
            
            setShowExpandButton(shouldShowExpand);
        } else {
            setShowExpandButton(false);
        }
    }, [introductionText, hasIntroductionText]);

    const handleToggleExpand = () => {
        const newExpandedState = !isExpanded;
        console.log('🔄 Section: Toggling expand state:', { 
            from: isExpanded, 
            to: newExpandedState,
            showExpandButton 
        });
        setIsExpanded(newExpandedState);
    };

    const handleOpenIntroductionModal = () => {
        previewSectionBaseTextModalActions.open({
            sectionData,
            canGenerateDocument: canEditDocument,
        });
    };

    const handleOpenEditIntroductionModal = () => {
        sectionIntroductionTextModalActions.open({
            sectionData,
            sectionMeta,
            canEditDocument,
        });
    };

    const sortedVariants = useMemo(() => variants.slice().sort((a, b) => {
        const isADefault = a.isDefault;
        const isBDefault = b.isDefault;

        if (isADefault && !isBDefault) return -1;
        if (!isADefault && isBDefault) return 1;

        return 0;
    }), [variants]);

    const updateIsDefaultTonggle = (variantId: number) => {
        const updateIds: number[] = [];
        const newDefaults = { ...variantisDefault };
        Object.keys(newDefaults).forEach((id) => {
            if (Number(id) === variantId) {
                newDefaults[variantId] = !newDefaults[variantId];
            } else if (newDefaults[Number(id)]) {
                newDefaults[Number(id)] = false;
                updateIds.push(Number(id));
            }
        });
        setVariantsIsDefault(newDefaults);
    };

    const handleToggleDefault = (variantId: number, currentEditorContent?: string) => {
        // Update local state first
        updateIsDefaultTonggle(variantId);

        // Find the current variant and its new default status
        const currentVariant = variants.find((v) => v.id === variantId);
        if (!currentVariant) return;

        const currentIsDefault = variantisDefault[variantId] || currentVariant.isDefault;
        const newIsDefault = !currentIsDefault;

        // Use the current editor content if provided, otherwise fall back to variant body
        const preservedBody = currentEditorContent || currentVariant.body || '';

        // Log warning if body is empty
        if (!preservedBody) {
            console.warn('⚠️ Variant body is empty when toggling default. This may indicate text content was lost.', {
                variantId,
                variant: currentVariant,
                sectionId: sectionData.id
            });
        }

        // Update the clicked variant
        const variantUpdateParams = {
            documentId: sectionData.document.id,
            chapterId: sectionData.chapter.id,
            sectionId: sectionData.id,
            id: variantId,
            isVisible: currentVariant.isVisible,
            body: preservedBody, // Always preserve existing body content
            isDefault: newIsDefault,
            brands: currentVariant.brands,
            regions: currentVariant.regions,
            areas: currentVariant.areas,
            countries: currentVariant.countries,
            propertyTypes: currentVariant.propertyTypes,
        };

        updateVariant(variantUpdateParams);

        // If setting this variant as default, unset all other default variants
        if (newIsDefault) {
            variants.forEach((variant) => {
                if (variant.id !== variantId && (variantisDefault[variant.id] || variant.isDefault)) {
                    // Preserve existing body content when updating other variants
                    // For other variants, we use their stored body since we don't have their editor content
                    const otherVariantPreservedBody = variant.body || '';

                    // Log warning if body is empty
                    if (!otherVariantPreservedBody) {
                        console.warn('⚠️ Other variant body is empty when toggling default. This may indicate text content was lost.', {
                            variantId: variant.id,
                            variant,
                            sectionId: sectionData.id
                        });
                    }

                    const otherVariantParams = {
                        documentId: sectionData.document.id,
                        chapterId: sectionData.chapter.id,
                        sectionId: sectionData.id,
                        id: variant.id,
                        isVisible: variant.isVisible,
                        body: otherVariantPreservedBody, // Always preserve existing body content
                        isDefault: false,
                        brands: variant.brands,
                        regions: variant.regions,
                        areas: variant.areas,
                        countries: variant.countries,
                        propertyTypes: variant.propertyTypes,
                    };

                    updateVariant(otherVariantParams);
                }
            });
        }
    };

    useEffect(() => {
        if (hash) {
            if (hash.startsWith('#variant-')) {
                const id = Number(hash.replace('#variant-', ''));
                const variantExists = variants.some((v) => v.id === id);
                
                if (!Number.isNaN(id) && variantExists) {
                    setSelectedVariantId(id);
                    setOpenVariantsIds((set) => {
                        const newSet = new Set(set);
                        newSet.add(id);
                        return newSet;
                    });
                }
            } else if (hash === '#variants') {
                if (!selectedVariantId && variants.length > 0) {
                    const firstVariant = sortedVariants[0];
                    if (firstVariant) {
                        setSelectedVariantId(firstVariant.id);
                        setOpenVariantsIds(new Set([firstVariant.id]));
                    }
                }
            }
        }
    }, [hash, variants, selectedVariantId, sortedVariants]);

    useEffect(() => {
        if (!variants.length) return;
        variants.forEach((variant) => {
            setOpenVariantsIds((set) => {
                const newSet = new Set(set);
                newSet.add(variant.id);
                return newSet;
            });
        });
    }, [variants]);

    useEffect(() => {
        const id = Number(hash.replace('#variant-', ''));
        let timer: any;

        if (!Number.isNaN(id)) {
            timer = setTimeout(() => {
                scrollToVariant(id);
            }, 150);
        }

        return () => {
            clearTimeout(timer);
        };
    }, [hash]);

    const actionButton = (
        <FloatingPublishButton
            sectionData={sectionData}
            sectionMeta={sectionMeta}
            top={top}
        />
    );

    const renderIntroductionSection = () => {
        if (!hasIntroductionText) {
            return (
                <div className="container max-constraint center">
                    <SectionIntroductionEmptyState
                        onEnableIntroduction={handleOpenEditIntroductionModal}
                        canEdit={canEditDocument}
                    />
                </div>
            );
        }

        return (
            <div className="container max-constraint center">
                <div className={cx('section-introduction-wrapper')}>
                    <div className={cx('section-introduction-header')}>
                        <div className={cx('title-section')}>
                            <div className={cx('title-header')}>
                                <h2 className={cx('introduction-title')}>Section Base Text</h2>
                                <button
                                    type="button"
                                    className={cx('help-button')}
                                    onClick={() => documentStructureHelpModalActions.open()}
                                    title="Learn how document structure works"
                                    aria-label="Learn how document structure works"
                                >
                                    <Info size={16} />
                                </button>
                            </div>
                            <p className={cx('introduction-description')}>
                                Base text appears first in every generated document for this section. Variants below are added only when their specific conditions are met.
                            </p>
                        </div>
                        <div className={cx('header-actions')}>
                            {showExpandButton && (
                                <button
                                    type="button"
                                    className={cx('expand-button')}
                                    onClick={handleToggleExpand}
                                    aria-label={isExpanded ? 'Show less' : 'Show more'}
                                >
                                    {isExpanded ? 'Show less' : 'Show more'}
                                </button>
                            )}
                            <BasicButton
                                title="Edit"
                                variant="neutral"
                                size="small"
                                onClick={handleOpenEditIntroductionModal}
                            />
                        </div>
                    </div>
                    <div className={cx('section-introduction-content', {
                        'is-collapsed': !isExpanded && showExpandButton,
                        'is-expanded': isExpanded,
                    })}
                    >
                        <div className={cx('content-wrapper')}>
                            <DocumentPreviewPage
                                body={introductionText}
                                isLandscape={false}
                                key={`preview-${lastUpdateTime}`}
                            />
                        </div>
                        {!isExpanded && showExpandButton && (
                            <div className={cx('fade-overlay')} />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={cx('content')}>
            {/* Mandatory Modal */}
            {isMandatoryOpen && (
                <AlertDialog
                    open={isMandatoryOpen}
                    handleClose={() => setMandatoryOpen(false)}
                    dialogContent={(
                        <Mandatory
                            sectionData={sectionData}
                            sectionMeta={sectionMeta}
                        />
                    )}
                    size="lg"
                />
            )}

            {/* Section Header */}
            <div className={cx('page-header-wrapper')}>
                <PageHeader
                    sectionData={sectionData}
                    sectionMeta={sectionMeta}
                    actionButton={actionButton}
                />
            </div>

            {/* Introduction Text Section */}
            {renderIntroductionSection()}

            {/* Divider */}
            <div className={cx('variants-divider')} />

            {/* Variants Content */}
            <div className="container max-constraint center">
                <div className={cx('variants-header')}>
                    <div className={cx('variants-title-wrapper')}>
                        <h2 className={cx('variants-title')}>Section Variants</h2>
                        <button
                            type="button"
                            className={cx('section-variant-info-button')}
                            onClick={() => setShowSectionVariantHelp(!showSectionVariantHelp)}
                            title="Learn more about section variants"
                            aria-label="Learn more about section variants"
                        >
                            <Info size={16} />
                        </button>
                    </div>
                    <div className={cx('variants-actions')}>
                        {/* Add any variant actions here if needed */}
                    </div>
                </div>

                {/* Section Variant Help */}
                {showSectionVariantHelp && (
                    <div className={cx('section-variant-explanation')}>
                        <div className={cx('explanation-header')}>
                            <div className={cx('explanation-icon')}>
                                <Info size={16} />
                            </div>
                            <div className={cx('explanation-title')}>What are Section Variants?</div>
                        </div>
                        <div className={cx('explanation-content')}>
                            <div className={cx('explanation-description')}>
                                Section variants enable dynamic, rule-based document generation by allowing different content to be displayed based on specific criteria. This powerful feature helps create personalized documents that adapt to different scenarios automatically.
                            </div>
                            <ul className={cx('explanation-features')}>
                                <li className={cx('explanation-feature')}>
                                    <div className={cx('feature-bullet')} />
                                    <span><strong>Applicability Settings:</strong> Each variant has configurable rules that determine when it should be applied during document generation</span>
                                </li>
                                <li className={cx('explanation-feature')}>
                                    <div className={cx('feature-bullet')} />
                                    <span><strong>Default Fallback:</strong> A default variant serves as fallback content when no specific variant matches the criteria</span>
                                </li>
                                <li className={cx('explanation-feature')}>
                                    <div className={cx('feature-bullet')} />
                                    <span><strong>Smart Replacement:</strong> When multiple variants could apply, the most specific one takes precedence over the default</span>
                                </li>
                                <li className={cx('explanation-feature')}>
                                    <div className={cx('feature-bullet')} />
                                    <span><strong>Use Case Example:</strong> Employee onboarding documents can show different content based on role, department, or location</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                )}

                <div className={cx('variants-content')}>
                    <div className={cx('master-detail-layout')}>
                        <div className={cx('sidebar-section')}>
                            <VariantListSidebar
                                sectionData={sectionData}
                                sectionMeta={sectionMeta}
                                selectedVariantId={selectedVariantId}
                                onVariantSelect={(variantId: number) => {
                                    setSelectedVariantId(variantId);
                                    setOpenVariantsIds(new Set([variantId]));
                                }}
                                variantDefaults={variantisDefault}
                            />
                        </div>

                        <div className={cx('main-content-section')}>
                            {selectedVariantId ? (
                                <div className={cx('selected-variant-wrapper')}>
                                    {sortedVariants
                                        .filter((variant) => variant.id === selectedVariantId)
                                        .map((variant, index) => {
                                            const isDefault = variantisDefault[variant.id] || variant.isDefault;
                                            
                                            const hasDefault = Object.values(variantisDefault).some((defaultValue) => defaultValue) 
                                                              || variants.some((v) => v.isDefault);
                                            
                                            const isDisable = hasDefault && !isDefault;

                                            return (
                                                <div
                                                    key={variant.id}
                                                    className={cx('variant-item-wrapper')}
                                                >
                                                    <Variant
                                                        variant={{ 
                                                            ...variant, 
                                                            variantIndex: `${variant.id}-${index}`, 
                                                            isDefault,
                                                            isDisable,
                                                        }}
                                                        handleToggleDefault={handleToggleDefault}
                                                        sectionData={sectionData}
                                                        sectionMeta={sectionMeta}
                                                    />
                                                </div>
                                            );
                                        })}
                                </div>
                            ) : (
                                <div className={cx('no-variant-selected')}>
                                    <div className={cx('no-variant-content')}>
                                        <div className={cx('no-variant-icon')}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                                                <polyline points="14,2 14,8 20,8" />
                                                <line x1="16" y1="13" x2="8" y2="13" />
                                                <line x1="16" y1="17" x2="8" y2="17" />
                                                <polyline points="10,9 9,9 8,9" />
                                            </svg>
                                        </div>
                                        <h3 className={cx('no-variant-title')}>Select a Variant to Edit</h3>
                                        <p className={cx('no-variant-description')}>
                                            Choose a variant from the sidebar to start editing its content, or create a new variant to get started.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

Content.propTypes = {
    view: PropTypes.oneOf(['options', 'sections']).isRequired,
    top: PropTypes.number.isRequired,
};

type Props = InferProps<typeof Content.propTypes> & {
    sectionData: AdminSingleSectionData;
    sectionMeta: AdminSingleSectionMeta;
};

export default Content;
